// Web Push (FCM) registration + foreground message handler.
//
// Usage:
//   import { ensurePushRegistered, onForegroundPush, unregisterPush } from "./push";
//   await ensurePushRegistered();   // requests permission, gets token, sends to backend
//   onForegroundPush((payload) => { ... });
//
// The service worker at /firebase-messaging-sw.js handles BACKGROUND messages.
// Foreground (tab open) messages route here so we can show an in-app toast +
// play a short chime via WebAudio.
import { getToken, onMessage } from "firebase/messaging";
import { toast } from "sonner";
import { getFirebaseMessaging, VAPID_KEY } from "./firebase";
import { api } from "./api";

const SW_URL = "/firebase-messaging-sw.js";
const TOKEN_KEY = "eplay_fcm_token";

export function pushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function registerSW() {
  try {
    const reg = await navigator.serviceWorker.register(SW_URL);
    // Wait until active so getToken can use it
    if (reg.installing) {
      await new Promise((res) => {
        reg.installing.addEventListener("statechange", (e) => {
          if (e.target.state === "activated") res();
        });
        setTimeout(res, 4000);
      });
    }
    return reg;
  } catch (err) {
    console.error("[push] SW register failed", err);
    return null;
  }
}

/**
 * Idempotent: requests permission (if needed), fetches FCM token, posts to backend.
 * Returns the token or null if anything (permission, support) failed.
 */
export async function ensurePushRegistered({ silent = false } = {}) {
  if (!pushSupported()) return null;
  if (!VAPID_KEY) return null;

  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") {
    if (!silent) toast.error("Notifications blocked — enable in browser settings");
    return null;
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) return null;

  const reg = await registerSW();
  if (!reg) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    if (!token) return null;
    const cached = window.localStorage.getItem(TOKEN_KEY);
    if (cached !== token) {
      await api.post("/push/register", { token, platform: "web" });
      window.localStorage.setItem(TOKEN_KEY, token);
    }
    return token;
  } catch (err) {
    console.error("[push] getToken failed", err);
    return null;
  }
}

export async function unregisterPush() {
  const cached = window.localStorage.getItem(TOKEN_KEY);
  if (cached) {
    try { await api.post("/push/unregister", { token: cached }); } catch {}
    window.localStorage.removeItem(TOKEN_KEY);
  }
}

let _foregroundBound = false;
let _audioCtx = null;
function playChime() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const t = ctx.currentTime;
    const beep = (start, freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t + start);
      gain.gain.setValueAtTime(0.0001, t + start);
      gain.gain.exponentialRampToValueAtTime(0.18, t + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + start + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t + start);
      osc.stop(t + start + 0.25);
    };
    beep(0, 880);
    beep(0.18, 1320);
  } catch {/* noop */}
}

/**
 * Bind a foreground message listener. Returns an unsubscribe.
 * Optional onPayload(payload) callback runs after the toast+chime.
 */
export async function onForegroundPush(onPayload) {
  if (_foregroundBound) return () => {};
  const messaging = await getFirebaseMessaging();
  if (!messaging) return () => {};
  _foregroundBound = true;
  return onMessage(messaging, (payload) => {
    const data = payload?.data || {};
    const title = data.title || payload?.notification?.title || "ePlay";
    const body = data.body || payload?.notification?.body || "";
    const link = data.link || "/";
    playChime();
    toast(title, {
      description: body,
      action: link
        ? { label: "Open", onClick: () => { window.location.href = link; } }
        : undefined,
    });
    if (typeof onPayload === "function") onPayload(payload);
  });
}

export async function sendTestPush() {
  try {
    await api.post("/push/test");
    toast.success("Test push sent — check your device");
  } catch (err) {
    toast.error("Push not enabled yet");
  }
}
